import { Event } from './EventTypes';
import type { EventKey, EventType } from './EventTypes';

type TranslationConfigByEvent = {
  [K in EventKey]: {
    fields: readonly (keyof EventType[K])[];
    fallbacks?: Record<string, string>;
  };
};

const translationConfigByEvent: TranslationConfigByEvent = {
  [Event.EDITED]: { fields: ['author', 'documentName'] },
  [Event.NOT_VIEWED]: { fields: ['documentName', 'viewedTime'] },
  [Event.VIEWED]: {
    fields: ['viewerName', 'companyName', 'documentName'],
    fallbacks: {
      companyName: 'notifications_feed.viewed_event_company_fallback',
    },
  },
  [Event.SHARED]: { fields: ['author', 'documentName', 'recipientsCount', 'recipient', 'collaboratorType'] },
  [Event.COMMENTED]: {
    fields: ['author', 'documentName'],
    fallbacks: {
      author: 'notifications_feed.commented_event_author_fallback',
      documentName: 'notifications_feed.commented_event_document_fallback',
    },
  },
  [Event.DOWNLOADED]: { fields: ['author', 'documentName'] },
  [Event.UPDATED]: { fields: ['author', 'documentName', 'status'] },
  [Event.REQUESTED]: { fields: ['author', 'documentName', 'approver'] },
  [Event.APPROVED]: { fields: ['author', 'documentName'] },
  [Event.DELETED]: { fields: ['author', 'documentName'] },
  [Event.RESTORED]: { fields: ['author', 'documentName'] },
  [Event.MOVED]: { fields: ['author', 'documentName', 'folderName'] },
  [Event.SIGNED]: { fields: ['author', 'documentName', 'status'] },
  [Event.VOIDED]: { fields: ['author', 'documentName'] },
  [Event.WON]: { fields: ['author', 'documentName'] },
  [Event.LOST]: { fields: ['author', 'documentName'] },
  [Event.AUTOMATED]: { fields: ['documentName', 'reminder'] },
  [Event.EXPIRED]: { fields: ['documentName'] },
  [Event.CREATED_FROM_TEMPLATE]: { fields: ['author', 'documentName', 'templateName'] },
  [Event.DUPLICATE]: { fields: ['author', 'documentName', 'duplicateDocumentName'] },
  [Event.CREATED]: { fields: ['author', 'documentName'] },
};

type TranslationConfig = {
  fields: readonly string[];
  fallbacks?: Record<string, string>;
};

export function getTranslationValues<K extends EventKey>(
  event: { type: K } & EventType[K],
  t?: (translationKey: string) => string
): EventType[K] {
  const config: TranslationConfig = translationConfigByEvent[event.type];
  const record = event;

  const values = {};
  for (const field of config.fields) {
    values[field] = record[field];
  }

  if (config.fallbacks) {
    for (const [field, translationKey] of Object.entries(config.fallbacks)) {
      if (!translationKey) continue;
      const current = values[field];
      const trimmed = typeof current === 'string' ? current.trim() : '';
      values[field] = trimmed || (t?.(translationKey) ?? '');
    }
  }

  return values as EventType[K];
}

export function isEventTranslationValues<K extends EventKey>(values: EventType[EventKey], type: K): values is EventType[K] {
  const config: TranslationConfig = translationConfigByEvent[type];
  return config.fields.every((field) => field in values);
}
